import { GlobalMessages } from '@shared/localization/messages';
import { useCallback, useMemo } from 'react';

import { useEstimationQueryActions } from '@/api/hooks/estimationQueryActions';
import type { EstimationResponse } from '@/api/schema/data-contracts';
import { useToast } from '@/context/toast';
import { useMessages } from '@/hooks/messages';

interface ResourceTypeNameRef {
  id: string;
  name: string;
}

interface UseResourceDisplayNameRenameParams {
  // Optional so the hook can be called before the page's loading guards resolve (rules of hooks).
  estimation: EstimationResponse | undefined;
  projectName: string | undefined;
  /** Resource types (grouped + ungrouped) in display order, used to fall back to default names. */
  sortedResourceTypes: ResourceTypeNameRef[];
}

/**
 * Shared logic for renaming per-estimation resource display names (column headers).
 * Used by the Resourcing tab and the Resource Cost tab (EWM-738) so the persistence
 * behaviour stays identical across both screens.
 */
export const useResourceDisplayNameRename = ({
  estimation,
  projectName,
  sortedResourceTypes,
}: UseResourceDisplayNameRenameParams) => {
  const toast = useToast();
  const globalMessages = useMessages(GlobalMessages);
  const { useUpdateEstimation } = useEstimationQueryActions();
  const updateEstimationMutation = useUpdateEstimation();

  const effectiveResourceDisplayNames = useMemo(
    () =>
      sortedResourceTypes.reduce<Record<string, string>>((acc, resourceType) => {
        acc[resourceType.id] =
          estimation?.resourceDisplayNames?.[resourceType.id] ?? resourceType.name;
        return acc;
      }, {}),
    [sortedResourceTypes, estimation?.resourceDisplayNames],
  );

  const hasPersistedOverrides = useMemo(
    () => Object.keys(estimation?.resourceDisplayNames ?? {}).length > 0,
    [estimation?.resourceDisplayNames],
  );

  const persist = useCallback(
    async (resourceDisplayNames: Record<string, string>) => {
      if (!estimation || projectName === undefined) return;
      try {
        await updateEstimationMutation.mutateAsync({
          estimationId: estimation.id,
          updateProjectWithEstimation: {
            project: { name: projectName },
            estimation: {
              versionName: estimation.versionName,
              unit: estimation.unit,
              projectDescription: estimation.projectDescription,
              resourceDisplayNames,
            },
          },
        });
        toast.showToast(globalMessages.savedSuccessfully(), 'success');
      } catch {
        toast.showToast(globalMessages.unexpectedError(), 'error');
      }
    },
    [estimation, projectName, toast, globalMessages, updateEstimationMutation],
  );

  const onResourceDisplayNameRename = useCallback(
    async (resourceTypeId: string, inputValue: string) => {
      const currentValue = inputValue.trim();
      if (!currentValue) return;

      const persistedValue =
        estimation?.resourceDisplayNames?.[resourceTypeId] ??
        sortedResourceTypes.find((rt) => rt.id === resourceTypeId)?.name;
      if (persistedValue === currentValue) return;

      const nextResourceDisplayNames = {
        ...effectiveResourceDisplayNames,
        [resourceTypeId]: currentValue,
      };
      const resourceDisplayNames = sortedResourceTypes.reduce<Record<string, string>>(
        (acc, resourceType) => {
          const value = nextResourceDisplayNames[resourceType.id]?.trim();
          if (value) acc[resourceType.id] = value;
          return acc;
        },
        {},
      );

      await persist(resourceDisplayNames);
    },
    [estimation?.resourceDisplayNames, sortedResourceTypes, effectiveResourceDisplayNames, persist],
  );

  const resetResourceDisplayNames = useCallback(() => persist({}), [persist]);

  return {
    effectiveResourceDisplayNames,
    hasPersistedOverrides,
    onResourceDisplayNameRename,
    resetResourceDisplayNames,
    isPending: updateEstimationMutation.isPending,
  };
};
